import { Skeleton } from "@/components/ui/skeleton";
import { Card } from "@/components/ui/card";

export const DashboardCardSkeleton = () => {
  return (
    <Card className="relative overflow-hidden rounded-2xl border-2 p-6 md:p-7 space-y-4 md:space-y-5 bg-gradient-to-br from-background/95 via-background/90 to-muted/50 backdrop-blur-sm">
      {/* Shimmer effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-shimmer bg-[length:200%_100%]" />
      
      {/* Icon and highlight area */}
      <div className="flex items-start justify-between relative z-10">
        <div className="relative">
          <Skeleton className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10" />
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/20 to-transparent animate-pulse" />
        </div>
        <Skeleton className="w-16 h-6 rounded-xl bg-muted/60" />
      </div>
      
      {/* Title and description */}
      <div className="space-y-3 relative z-10">
        <Skeleton className="h-6 w-3/4 bg-gradient-to-r from-muted/80 to-muted/60 rounded-lg" />
        <Skeleton className="h-4 w-full bg-gradient-to-r from-muted/60 to-muted/40 rounded-md" />
        <Skeleton className="h-4 w-2/3 bg-gradient-to-r from-muted/60 to-muted/40 rounded-md" />
      </div>
      
      {/* Decorative elements */}
      <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-primary/10 to-transparent rounded-full animate-pulse" />
      <div className="absolute -bottom-2 -left-2 w-6 h-6 bg-gradient-to-br from-secondary/10 to-transparent rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
    </Card>
  );
};

export const DashboardSkeleton = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30 relative overflow-hidden">
      {/* Enhanced background patterns */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5 pointer-events-none" />
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-radial from-primary/10 to-transparent rounded-full blur-3xl pointer-events-none animate-pulse" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-radial from-secondary/10 to-transparent rounded-full blur-3xl pointer-events-none animate-pulse" style={{ animationDelay: '2s' }} />
      
      <div className="relative z-10 space-y-8 md:space-y-12 animate-fadeIn px-4 sm:px-6 md:px-8 lg:px-10 pt-6 pb-12">
        {/* Hero Section Skeleton */}
        <div className="text-center space-y-6 pt-4 sm:pt-8">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="p-4 bg-gradient-to-br from-primary/20 to-primary/10 rounded-2xl animate-bounce">
              <Skeleton className="h-8 w-8 bg-primary/20" />
            </div>
            <div className="h-8 w-px bg-gradient-to-b from-transparent via-primary/20 to-transparent" />
            <div className="p-4 bg-gradient-to-br from-secondary/20 to-secondary/10 rounded-2xl animate-bounce" style={{ animationDelay: '0.5s' }}>
              <Skeleton className="h-8 w-8 bg-secondary/20" />
            </div>
          </div>
          
          <div className="space-y-4">
            <Skeleton className="h-12 md:h-16 w-2/3 mx-auto rounded-2xl bg-gradient-to-r from-primary/20 via-primary/10 to-secondary/20" />
            <div className="flex items-center justify-center gap-2">
              <Skeleton className="h-5 w-5 rounded-full bg-primary/20" />
              <Skeleton className="h-6 w-20 rounded-lg bg-muted/60" />
            </div>
            <Skeleton className="h-6 w-1/2 mx-auto rounded-xl bg-muted/40" />
          </div>
          
          {/* Quick Stats Skeleton */}
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-8 pt-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-2 px-4 py-2 bg-background/80 backdrop-blur-sm rounded-full border border-primary/20 shadow-sm">
                <Skeleton className="h-4 w-4 rounded-full bg-primary/20" />
                <Skeleton className="h-4 w-20 bg-muted/40" />
              </div>
            ))}
          </div>
        </div>
        
        {/* Quick Actions Hub Skeleton */}
        <div className="w-full">
          <Card className="p-6 bg-gradient-to-br from-background/95 via-background/90 to-muted/50 backdrop-blur-sm border-2 border-primary/10">
            <div className="flex items-center justify-between mb-6">
              <Skeleton className="h-6 w-32 bg-primary/20" />
              <Skeleton className="h-4 w-24 bg-muted/40" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex flex-col items-center p-4 rounded-xl bg-background/50 border border-primary/10">
                  <Skeleton className="h-10 w-10 rounded-xl bg-primary/20 mb-3" />
                  <Skeleton className="h-4 w-16 bg-muted/40" />
                </div>
              ))}
            </div>
          </Card>
        </div>
        
        {/* Features Grid Skeleton */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8">
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} style={{ animationDelay: `${index * 0.1}s` }}>
              <DashboardCardSkeleton />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};